import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  ColumnDefResolved,
  ColumnFiltersState,
  RowSelectionState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp } from "lucide-react";
import { CustomColumnDef, CustomColumnMeta } from "@lib/types/column";
import { cn } from "@lib/utils/tailwind";

interface TableListProps<TData, TValue> {
  isLoading?: boolean;
  columns: CustomColumnDef<TData, TValue>[];
  data: TData[];
  rowSelection: RowSelectionState;
  setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>;
}

const TableList = <TData, TValue>({
  isLoading,
  columns,
  data,
  rowSelection,
  setRowSelection,
}: TableListProps<TData, TValue>) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [hiddenColumns, setHiddenColumns] = useState<
    { header: string; accessorKey: string }[]
  >([]);

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      pagination: { pageIndex: page, pageSize: rowsPerPage },
    },
    pageCount: Math.ceil(data.length / rowsPerPage),
  });

  useEffect(() => {
    const handleResize = () => {
      const newVisibility: VisibilityState = {};
      const hiddenCols: { header: string; accessorKey: string }[] = [];

      table.getAllColumns().forEach((column) => {
        const meta = column.columnDef.meta as CustomColumnMeta;
        if (meta?.hideOnSmallScreens) {
          newVisibility[column.id] = window.innerWidth > 1024; // Show only if screen width is 1024px or more

          if (!newVisibility[column.id] && column.id !== "select") {
            const columnDef = column.columnDef as ColumnDefResolved<
              TData,
              TValue
            >;
            hiddenCols.push({
              header: columnDef.header as string,
              accessorKey: columnDef.accessorKey as string,
            });
          }
        }
      });

      setColumnVisibility((prev) => ({ ...prev, ...newVisibility }));
      setHiddenColumns(hiddenCols);
    };

    handleResize(); // Run on mount
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [table]);

  const toggleRowExpand = (rowId: string) => {
    setExpandedRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  };

  const getNestedValue = (obj: any, path: string): any => {
    const value = path.split(".").reduce((acc, key) => acc?.[key], obj);

    // Check if value is a valid date
    if (value && typeof value === "string" && !isNaN(Date.parse(value))) {
      return new Date(value).toLocaleString();
    }

    return value;
  };

  return (
    <TableContainer
      component={Paper}
      className="flex flex-col max-w-full mx-auto my-4"
    >
      <Table>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="bg-gray-300">
              {/* Space for the accordion button */}
              <TableCell className="flex lg:hidden" />
              {headerGroup.headers.map((header) => (
                <TableCell key={header.id} className={cn("font-semibold p-2")}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {!isLoading && table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <React.Fragment key={row.id}>
                <TableRow>
                  <TableCell
                    key="toggle"
                    className="table-cell h-full lg:hidden p-2"
                  >
                    <IconButton onClick={() => toggleRowExpand(row.id)}>
                      {expandedRows[row.id] ? <ChevronDown /> : <ChevronUp />}
                    </IconButton>
                  </TableCell>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="p-2">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow className="table-row lg:hidden">
                  <TableCell
                    colSpan={row.getVisibleCells().length + 1}
                    className="p-0"
                  >
                    <Collapse in={expandedRows[row.id]}>
                      <div className="p-4 bg-gray-100">
                        {/* Hidden row details */}

                        {hiddenColumns.map((column, index) => {
                          const value = column.accessorKey
                            ? getNestedValue(row.original, column.accessorKey)
                            : "-";

                          return (
                            <span key={index} className="p-2 block">
                              <strong>{column.header}:</strong> {value ?? "-"}
                            </span>
                          );
                        })}
                      </div>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length + 1} className="text-center">
                {isLoading ? t("general.loading") : t("general.noData")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 20, 50]}
        component="div"
        count={data.length || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) =>
          setRowsPerPage(parseInt(e.target.value, 10))
        }
        labelRowsPerPage={t("table.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          `${from} - ${to} ${t("table.of")} ${count}`
        }
      />
    </TableContainer>
  );
};

export default TableList;
